import React, { useEffect, useState, useContext } from 'react';

import { Typography, Box, Grid2 as Grid, Container, CircularProgress } from '@mui/material';

import Train from './components/Train';
import { OccupancyState } from './components/icons/Occupancy';

import { Context } from './Context';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./firebase";
import { normalizeCars } from "./utils/normalizeCars";

// The dashboard reports Stuttgart operations, so the clock is Berlin time for
// every viewer regardless of where they are. This must be stated explicitly: a
// 'de-DE' locale sets FORMATTING only (weekday names, 24h clock) and leaves the
// timezone as the browser's, which rendered UK time under a German label when
// viewed from England.
const DISPLAY_TZ = 'Europe/Berlin';

// Schedule times are ISO 8601 with offset, written by the sweep. Rendered in
// Berlin time for the same reason as the clock: the reader's location is
// irrelevant to when a Stuttgart train runs.
const hhmm = (iso?: string | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString('de-DE', { timeZone: DISPLAY_TZ, hour: '2-digit', minute: '2-digit' });
};

const useClock = () => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return now;
};

type Dataset = {
    line: {
        number: number,
        route: string
    }
    // Written by the sweep (see sweep/firestore_sync.py _OWNED_FIELDS), so these
    // are present even for journeys with no MGW feed — which is exactly the case
    // where `line.route` is absent, because the aggregator never wrote it.
    origin_name?: string | null
    destination_name?: string | null
    scheduled_departure_time?: string | null
    scheduled_arrival_time?: string | null
    status?: string | null
    live: {
        station: string,
        time: string,
        delay: number,
        occupancy: number,
        occupancy_level?: OccupancyState,
        passengers: number,
        cars: OccupancyState[][],
        sched_arrival?: string | null,
        sched_departure?: string | null,
        obs_arrival?: string | null,
        obs_departure?: string | null,
        delay_arrival_sec?: number | null,
        delay_departure_sec?: number | null
    }
    prognoses: {
        station: string,
        time: string,
        delay: number,
        occupancy: number,
        occupancy_level?: OccupancyState,
        passengers: number,
        cars: OccupancyState[][],
        forecast?: boolean,
        sched_arrival?: string | null,
        sched_departure?: string | null,
        obs_arrival?: string | null,
        obs_departure?: string | null,
        delay_arrival_sec?: number | null,
        delay_departure_sec?: number | null,
        stop_sequence?: number | null
    }[]
}


const LiveView = () => {
    const now = useClock();
    const timeString = now.toLocaleTimeString('de-DE', { timeZone: DISPLAY_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const dateString = now.toLocaleDateString('de-DE', { timeZone: DISPLAY_TZ, weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const [dataset, setDataset] = useState<null | Dataset>(null);
    const [loading, setLoading] = useState(false);
    const theme = useTheme();
    const hideScroll = useMediaQuery(theme.breakpoints.up('lg'));

    const { selected } = useContext(Context);

    useEffect(() => {
        if (!selected) {
            setDataset(null);
            setLoading(false);
            return;
        }
        console.log("selected =", selected);

        setLoading(true);
        const { lineId, routeId } = selected;

        const ref = doc(db, "lines", lineId, "routes", routeId);
        const unsub = onSnapshot(ref, (snap) => {
            if (!snap.exists()) {
                setDataset(null);
                setLoading(false);
                return;
            }

            const raw: any = snap.data();

            const patched: Dataset = {
                ...raw,
                live: raw?.live
                    ? { ...raw.live, cars: normalizeCars(raw.live.cars) as any }
                    : raw.live,
                prognoses: Array.isArray(raw?.prognoses)
                    ? raw.prognoses.map((p: any) => ({
                        ...p,
                        cars: normalizeCars(p?.cars) as any,
                    }))
                    : [],
            };

            setDataset(patched);
            setLoading(false);
        },
            (err) => {
                console.error("❌ route onSnapshot error", err);
                setLoading(false);
            }
        );

        return () => unsub();
    }, [selected]);


    // Loading state
    if (loading) {
        return (
            <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: "0 auto", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box textAlign="center">
                    <CircularProgress sx={{ color: '#18364a' }} />
                    <Typography fontSize={15} color="#888" mt={2}>Daten werden geladen…</Typography>
                </Box>
            </Container>
        );
    }

    // No route selected state
    if (!selected) {
        return (
            <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: "0 auto", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography fontSize={15} color="#888">Keine aktiven Züge verfügbar.</Typography>
            </Container>
        );
    }

    // No data for selected route
    if (!dataset) {
        return (
            <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: "0 auto", display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography fontSize={15} color="#888">Keine Daten für diesen Zug.</Typography>
            </Container>
        );
    }
    // Origin/destination with scheduled times: "Weil der Stadt (17:00) → Stuttgart Hbf (18:10)".
    // Falls back to the aggregator's prebuilt string when the sweep fields are absent.
    // Not a hook, so it is safe below the early returns above.
    const schedDep = hhmm(dataset.scheduled_departure_time);
    const schedArr = hhmm(dataset.scheduled_arrival_time);
    const routeLabel =
        dataset.origin_name || dataset.destination_name
            ? `${dataset.origin_name ?? ''}${schedDep ? ` (${schedDep})` : ''}`
              + ` \u2192 `
              + `${dataset.destination_name ?? ''}${schedArr ? ` (${schedArr})` : ''}`
            : (dataset.line?.route ?? '');

    const clockBar = (
        <Box sx={{ width: 710, margin: '0 auto 12px', display: 'flex', justifyContent: 'flex-end' }}>
            <Typography fontSize={13} color="#666">
                {dateString} · {timeString} <span style={{ color: '#aaa' }}>(Ortszeit Deutschland)</span>
            </Typography>
        </Box>
    );

    return (
        <>
            {clockBar}
            <Container sx={{ height: 894, width: 710, boxShadow: 3, borderRadius: 2, margin: "0 auto", overflowY: 'hidden' }}>

            <Grid container spacing={2} sx={{ paddingTop: '34px' }}>
                <Grid size={2}>
                    <Typography fontSize={18.5} fontWeight={'bold'} align="left">Live:</Typography>
                </Grid>
                <Grid size={3} offset={1}>
                    <Typography fontSize={18.5} align="right">
                        <span style={{ fontWeight: 'bold' }}>Zug</span>: {dataset.line?.number || ''}
                    </Typography>
                </Grid>
                <Grid size="grow">
                    <Typography fontSize={18.5} align="right">{routeLabel}</Typography>
                </Grid>
            </Grid>

            <Box border="2px solid #dddddd" borderRadius={'3px'} padding={'0px 10px 5px 6px'} marginTop="34px">
                <Train
                    sx={{ paddingTop: "0px" }}
                    cars={dataset.live?.cars ?? []}
                    station={dataset.live?.station ?? ''}
                    passengers={dataset.live?.passengers ?? 0}
                    time={dataset.live?.time ?? ''}
                    delay={dataset.live?.delay ?? 0}
                    occupancy={dataset.live?.occupancy ?? 0}
                    occupancyLevel={dataset.live?.occupancy_level}
                    schedArrival={dataset.live?.sched_arrival}
                    schedDeparture={dataset.live?.sched_departure}
                    obsArrival={dataset.live?.obs_arrival}
                    obsDeparture={dataset.live?.obs_departure}
                    delayArrivalSec={dataset.live?.delay_arrival_sec}
                    delayDepartureSec={dataset.live?.delay_departure_sec}
                />
            </Box>

            <Typography fontSize={18.5} fontWeight={'bold'} align="left" marginTop="34px">Prognosen</Typography>
            <Box style={{ overflow: 'hidden', position: 'relative' }}>
                <Box style={{ overflow: 'auto', height: '665px', width: hideScroll ? '665px' : '100%' }}>
                {dataset.prognoses.map((data, index) => (
                    <Box key={`${data.station}-${data.time}-${index}`} padding={'0px 5px 0px 5px'}>
                        <Train sx={{}} station={data.station} time={data.time} delay={data.delay} occupancy={data.occupancy} occupancyLevel={data.occupancy_level} passengers={data.passengers} cars={data.cars} silhouette={data.forecast === false} terminus={index === dataset.prognoses.length - 1}
                            forecast={data.forecast}
                            schedArrival={data.sched_arrival}
                            schedDeparture={data.sched_departure}
                            obsArrival={data.obs_arrival}
                            obsDeparture={data.obs_departure}
                            delayArrivalSec={data.delay_arrival_sec}
                            delayDepartureSec={data.delay_departure_sec}
                        />
                    </Box>
                ))}

                {dataset.prognoses.length < 7 && (
                    <Box padding={'0px 5px 0px 5px'} style={{ width: '100%', height: (100 + 100 * (7 - dataset.prognoses.length)) }} />
                )}
                <Box padding={'0px 5px 0px 5px'} style={{ width: '100%', height: 100 }} />
                </Box>
            </Box>

            </Container>
        </>
    );
};

export default LiveView;